import { IApplication, IOption as IModuleOption, ModuleOption } from '../../domain/portal-domain';
import { ApplicationIds } from '../../domain/application-ids';
import { ApiAdapter } from './api-adapter';
import { EmptyResponseDataError, FetchError } from './api-errors';
import { getAuthorizationOptions } from '../fetches';
import { toast } from '../../components/containers/toast';

export interface IModulesQueryResponse {
    [installationUri: string]: IApplication[];
}
export class ModulesApiAdapter extends ApiAdapter {
    private static instance: ModulesApiAdapter;

    public static getInstance = (): ModulesApiAdapter => {
        if (!ModulesApiAdapter.instance) {
            ModulesApiAdapter.instance = new ModulesApiAdapter();
        }
        return ModulesApiAdapter.instance;
    };

    private constructor() {
        super('User');
    }

    private getInstallationUri = (companyId: number, installationId: number): string => `${companyId}/${installationId}`;

    public async getAvailableInstallationModules(companyId: number, installationId: number): Promise<IModuleOption[]> {
        try {
            var authOptions = await getAuthorizationOptions();
            const response = await fetch(
                `${this.apiAddress}/CurrentApplications?companyId=${companyId}&installationId=${installationId}`,
                authOptions
            );
            const installations: IModulesQueryResponse = await response.json();
            if (!installations) {
                throw new EmptyResponseDataError('available installation modules');
            }
            const installationUri = this.getInstallationUri(companyId, installationId);
            const module: IApplication[] = installations[installationUri];
            const moduleOptions = module.map((m) => new ModuleOption(m.name, m.id));

            return moduleOptions;
        } catch (e) {
            throw new FetchError('Modules API', e);
        }
    }

    public async getAllApplicationsForInstallation(companyId: number, installationId: number): Promise<IApplication[]> {
        try {
            var authOptions = await getAuthorizationOptions();
            let response: Response;
            if (companyId && installationId) {
                response = await fetch(
                    `${this.apiAddress}/GetAllApplicationsForInstallation?companyId=${companyId}&installationId=${installationId}`,
                    authOptions
                );
            } else if (companyId) {
                response = await fetch(`${this.apiAddress}/GetAllApplicationsForCompany?companyId=${companyId}`, authOptions);
            } else {
                return [];
            }

            const applications: IApplication[] = await response.json();

            return applications.filter((x) => x.id !== ApplicationIds.FilteredApplication);
        } catch (e) {
            throw new FetchError('Applications API', e);
        }
    }

    public async updateSignatureForUser(userId: number, formData: FormData): Promise<any> {
        try {
            const authOptions = await getAuthorizationOptions();
            const response = await fetch(`${this.apiAddress}/UpdateSignatureForUser?userId=${userId}`, {
                method: 'POST',
                headers: {
                    ...authOptions.headers,
                },
                body: formData,
            });
            if (response.ok !== true && response.statusText !== 'OK') {
                toast.error(`Server responded with error: ${response.statusText}`);
                throw new Error(`Failed to upload signature: ${response.statusText}`);
            }

            await response.json();
            toast.success('Signature uploaded successfully!');
        } catch (e) {
            toast.error('An error occurred while uploading the signature.');
            throw new FetchError('UpdateSignatureForUser API', e);
        }
    }

    public async removeSignatureForUser(userId: number): Promise<any> {
        try {
            const authOptions = await getAuthorizationOptions();
            const response = await fetch(`${this.apiAddress}/RemoveSignatureForUser?userId=${userId}`, {
                method: 'DELETE',
                headers: {
                    ...authOptions.headers,
                },
            });

            if (!response.ok) {
                toast.error(`Error: ${response.status} ${response.statusText}`);
                throw new Error(`Failed to remove signature: ${response.statusText}`);
            }

            await response.json();
            toast.success('Signature removed successfully!');
        } catch (e) {
            toast.error('An error occurred while removing the signature.');
            throw new FetchError('RemoveSignatureForUser API', e);
        }
    }
}
